import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { fetchTeamAnalytics } from '../../api/teamAnalytics';
import AnalyticsSummaryCards from './AnalyticsSummaryCards';
import AnalyticsWinLoss from './AnalyticsWinLoss';
import AnalyticsRunScoring from './AnalyticsRunScoring';
import AnalyticsHomeAway from './AnalyticsHomeAway';
import AnalyticsOpponents from './AnalyticsOpponents';

const { FiX, FiBarChart2, FiTrendingUp, FiHome, FiUsers, FiRefreshCw } = FiIcons;

const TABS = [
  { id: 'winloss', label: 'Win/Loss', icon: FiTrendingUp },
  { id: 'scoring', label: 'Run Scoring', icon: FiBarChart2 },
  { id: 'homeaway', label: 'Home/Away', icon: FiHome },
  { id: 'opponents', label: 'Opponents', icon: FiUsers },
];

const TeamPerformanceAnalytics = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('winloss');

  const load = () => {
    setLoading(true);
    fetchTeamAnalytics(111)
      .then(setData)
      .catch(() => setError('Could not load analytics data.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-6"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        <motion.div
          className="relative w-full max-w-2xl bg-[#1e3a29] border-4 border-[#d8ceb3] shadow-2xl z-10 max-h-[94vh] flex flex-col"
          initial={{ scale: 0.92, y: 24 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.92, y: 24 }}
          transition={{ type: 'spring', stiffness: 280, damping: 24 }}
        >
          {/* Header */}
          <div className="bg-[#386646] border-b-2 border-[#658d71] p-3 sm:p-4 flex items-center justify-between shrink-0">
            <div>
              <p className="text-[9px] tracking-widest uppercase text-[#a9c9b1] font-bold">Boston Red Sox</p>
              <h2 className="text-base sm:text-xl font-black text-[#d8ceb3] uppercase tracking-widest">Team Performance Analytics</h2>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={load} disabled={loading} className="p-2 border border-[#658d71] text-[#a9c9b1] hover:bg-[#658d71] transition-colors">
                <SafeIcon icon={FiRefreshCw} size={14} className={loading ? 'animate-spin' : ''} />
              </button>
              <button onClick={onClose} className="p-2 border border-[#658d71] text-[#d8ceb3] hover:bg-[#658d71] transition-colors">
                <SafeIcon icon={FiX} size={18} />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-b-2 border-[#658d71] shrink-0 overflow-x-auto">
            {TABS.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-3 sm:px-4 py-2.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest transition-colors whitespace-nowrap border-r border-[#658d71] last:border-0 ${activeTab === tab.id ? 'bg-[#386646] text-[#d8ceb3]' : 'text-[#a9c9b1] hover:text-white hover:bg-[rgba(255,255,255,0.04)]'}`}>
                <SafeIcon icon={tab.icon} size={10} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-5">
            {loading && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-8 h-8 border-2 border-[#658d71] border-t-[#d8ceb3] rounded-full animate-spin" />
                <span className="text-[#a9c9b1] text-xs tracking-widest uppercase">Loading analytics...</span>
              </div>
            )}
            {error && <p className="text-center text-[#e6a8a8] text-sm py-8">{error}</p>}
            {!loading && !error && data && (
              <AnimatePresence mode="wait">
                <motion.div key={activeTab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.18 }} className="flex flex-col gap-4">
                  <AnalyticsSummaryCards allGames={data.allGames} />
                  {activeTab === 'winloss' && <AnalyticsWinLoss allGames={data.allGames} />}
                  {activeTab === 'scoring' && <AnalyticsRunScoring allGames={data.allGames} />}
                  {activeTab === 'homeaway' && <AnalyticsHomeAway allGames={data.allGames} />}
                  {activeTab === 'opponents' && <AnalyticsOpponents allGames={data.allGames} />}
                </motion.div>
              </AnimatePresence>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default TeamPerformanceAnalytics;